import React, { useState } from 'react';
import { LogIn, UserPlus, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useRecaptcha } from '../hooks/useRecaptcha';

interface RegisterData {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    createNewTenant?: boolean;
    tenantName?: string;
    tenantId?: number;
}

export default function LoginScreen() {
  const { login, register } = useAuth();
  const { executeRecaptcha } = useRecaptcha();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [createNewTenant, setCreateNewTenant] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha(isLogin ? 'login' : 'register');

      if (isLogin) {
        await login(email, password, recaptchaToken || undefined);
      } else {
        const registerData: RegisterData = {
          email,
          password,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
        };

        if (createNewTenant) {
          if (!tenantName.trim()) {
            setError('Please enter a tenant name');
            setLoading(false);
            return;
          }
          registerData.createNewTenant = true;
          registerData.tenantName = tenantName;
        } else if (tenantId) {
          registerData.tenantId = parseInt(tenantId);
        }

        await register(registerData, recaptchaToken || undefined);
      }
    } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6">
            <h1 className="text-3xl font-bold text-white text-center">
              MVNO Platform
            </h1>
            <p className="text-blue-100 text-center mt-2">
              Rate Plan Pricing Calculator
            </p>
          </div>

          <div className="p-8">
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => {
                  setIsLogin(true);
                  setError('');
                }}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                  isLogin
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <LogIn className="w-4 h-4 inline mr-2" />
                Login
              </button>
              <button
                onClick={() => {
                  setIsLogin(false);
                  setError('');
                }}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                  !isLogin
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <UserPlus className="w-4 h-4 inline mr-2" />
                Register
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-800 rounded-lg text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                  placeholder="••••••••"
                />
              </div>

              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className="border-2 border-gray-200 rounded-lg p-4">
                    <label className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        checked={createNewTenant}
                        onChange={(e) => setCreateNewTenant(e.target.checked)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Create New Tenant
                      </span>
                    </label>

                    {createNewTenant ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Tenant Name
                        </label>
                        <input
                          type="text"
                          value={tenantName}
                          onChange={(e) => setTenantName(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                          placeholder="My Organization"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Tenant ID (optional)
                        </label>
                        <input
                          type="number"
                          value={tenantId}
                          onChange={(e) => setTenantId(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                          placeholder="1 (Default)"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Leave empty to use default tenant
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Please wait...' : isLogin ? 'Login' : 'Register'}
              </button>
            </form>

            {isLogin && import.meta.env.MODE !== 'production' && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 font-semibold mb-2">
                  Default Credentials:
                </p>
                <p className="text-xs text-blue-700">
                  Email: admin@example.com<br />
                  Password: admin123
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-6 text-gray-600 text-sm">
          <p>Zynalytics Wireless Rate Plan Calculator v2.0</p>
          <p className="mt-1">© 2025 Zardoz Group. All rights reserved</p>
        </div>
      </div>
    </div>
  );
}